/** Valid package NDC shapes, not a claim of FDA listing or label identity. */
export function isValidNdcFormat(value: string): boolean {
  return /^(?:\d{10}|\d{11}|\d{12}|\d{4}-\d{4}-\d{2}|\d{5}-\d{3}-\d{2}|\d{5}-\d{4}-\d{1,2}|\d{6}-\d{4}-\d{2})$/.test(value);
}

/** Never parse NDC segments as numbers: leading zeroes identify the package. */
export function ndcBillingCandidates(input: string): string[] {
  const value = input.trim().replace(/\s+/g, "");
  if (/^\d{11}$/.test(value)) return [value];
  if (/^\d{12}$/.test(value)) return [value]; // FDA 6-4-2 identifiers remain distinct.
  if (/^\d{10}$/.test(value)) {
    return [...new Set([
      `0${value.slice(0, 4)}${value.slice(4, 8)}${value.slice(8)}`,
      `${value.slice(0, 5)}0${value.slice(5, 8)}${value.slice(8)}`,
      `${value.slice(0, 5)}${value.slice(5, 9)}0${value.slice(9)}`,
    ])];
  }
  if (!/^(?:\d{4}-\d{4}-\d{2}|\d{5}-\d{3}-\d{2}|\d{5}-\d{4}-\d{1,2}|\d{6}-\d{4}-\d{2})$/.test(value)) return [];
  const [labeler, product, pack] = value.split("-");
  return [labeler.padStart(labeler.length === 6 ? 6 : 5, "0") + product.padStart(4, "0") + pack.padStart(2, "0")];
}

export interface NdcCatalogRecord {
  id: number;
  name: string;
  ndc: string;
  inStock: boolean | null;
  publicEligible: boolean;
  completeness: number;
}

export function chooseCanonicalNdcProduct<T extends NdcCatalogRecord>(records: T[]): T {
  // Never send visitors to a blocked/unlisted product when a public record exists.
  const publicRecords = records.filter(record => record.publicEligible);
  const candidates = publicRecords.length ? publicRecords : records;
  const inStock = candidates.filter(record => record.inStock === true);
  if (inStock.length === 1) return inStock[0];
  return [...candidates].sort((a, b) => b.completeness - a.completeness || a.id - b.id)[0];
}

export function buildNdcCatalogIndex<T extends NdcCatalogRecord>(records: T[]): Map<string, T[]> {
  const index = new Map<string, T[]>();
  // A bare ten-digit stored value is ambiguous too. Do not invent its segments.
  for (const record of records.filter(r => !/^\d{10}$/.test(r.ndc.trim()))) {
    const keys = ndcBillingCandidates(record.ndc);
    if (keys.length !== 1) continue;
    index.set(keys[0], [...(index.get(keys[0]) || []), record]);
  }
  for (const record of records.filter(r => /^\d{10}$/.test(r.ndc.trim()))) {
    const keys = ndcBillingCandidates(record.ndc).filter(key => index.has(key));
    if (keys.length === 1) index.get(keys[0])!.push(record);
    else index.set(`raw:${record.ndc.trim()}`, [...(index.get(`raw:${record.ndc.trim()}`) || []), record]);
  }
  return index;
}

export function resolveCatalogNdc<T extends NdcCatalogRecord>(input: string, index: Map<string, T[]>) {
  const keys = ndcBillingCandidates(input);
  if (/^\d{10}$/.test(input.trim())) keys.push(`raw:${input.trim()}`);
  const matches = keys.flatMap(key => {
    const eligible = (index.get(key) || []).filter(p => p.publicEligible);
    return eligible.length ? [{ billingNdc: key, product: chooseCanonicalNdcProduct(eligible) }] : [];
  });
  return { kind: matches.length === 0 ? "not-found" : matches.length === 1 ? "match" : "ambiguous", matches } as const;
}
